/**
 * Contact Form
 *
 * Validates submissions client-side, prevents the native HTML5 validation,
 * and POSTs to the WP REST endpoint at /wp-json/wpsct/v1/contact-form. On
 * success, the form is replaced with the configured success message; on
 * error, inline messages render per-field plus a global status line.
 *
 * Each `[data-contact-form]` element is independent — multiple forms on the
 * same page work without coordination.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class ContactForm {
  constructor(form) {
    this.form = form;
    this.restUrl = form.getAttribute("data-rest-url") || "";
    this.successMessage = form.getAttribute("data-success-message") || "Sent.";
    this.errorMessage =
      form.getAttribute("data-error-message") || "Something went wrong.";
    this.recipient = form.getAttribute("data-recipient") || "";
    this.subjectTemplate = form.getAttribute("data-subject-template") || "";

    this.submitBtn = form.querySelector(".submit-btn");
    this.statusEl = form.querySelector(".form-status");

    this.fields = {
      name: form.querySelector('[name="name"]'),
      email: form.querySelector('[name="email"]'),
      message: form.querySelector('[name="message"]'),
    };

    this.bind();
  }

  bind() {
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));

    Object.entries(this.fields).forEach(([key, el]) => {
      if (!el) return;
      el.addEventListener("input", () => this.clearFieldError(key));
      el.addEventListener("blur", () => {
        const err = this.validateField(key, el.value);
        if (err) this.setFieldError(key, err);
      });
    });
  }

  validateField(key, raw) {
    const val = (raw || "").trim();
    if (key === "name") {
      if (!val) return "Please enter your name.";
      if (val.length > 100) return "Please keep your name under 100 characters.";
    }
    if (key === "email") {
      if (!val) return "Please enter your email.";
      if (!EMAIL_RE.test(val)) return "Please enter a valid email.";
    }
    if (key === "message") {
      if (val.length < 10) return "Please write at least 10 characters.";
      if (val.length > 5000) return "Please keep your message under 5000 characters.";
    }
    return "";
  }

  validateAll() {
    let firstInvalid = null;
    let hasErrors = false;

    Object.entries(this.fields).forEach(([key, el]) => {
      if (!el) return;
      const err = this.validateField(key, el.value);
      if (err) {
        this.setFieldError(key, err);
        hasErrors = true;
        if (!firstInvalid) firstInvalid = el;
      } else {
        this.clearFieldError(key);
      }
    });

    if (firstInvalid) firstInvalid.focus();
    return !hasErrors;
  }

  setFieldError(key, message) {
    const wrap = this.form.querySelector(`[data-field="${key}"]`);
    const target = this.form.querySelector(`[data-error-for="${key}"]`);
    if (wrap) wrap.classList.add("has-error");
    if (target) target.textContent = message;
  }

  clearFieldError(key) {
    const wrap = this.form.querySelector(`[data-field="${key}"]`);
    const target = this.form.querySelector(`[data-error-for="${key}"]`);
    if (wrap) wrap.classList.remove("has-error");
    if (target) target.textContent = "";
  }

  setStatus(message, kind) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.dataset.kind = kind || "";
    this.statusEl.hidden = !message;
  }

  setBusy(busy) {
    this.form.setAttribute("aria-busy", busy ? "true" : "false");
    if (this.submitBtn) this.submitBtn.disabled = busy;
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.setStatus("", "");

    if (!this.validateAll()) return;
    if (!this.restUrl) {
      this.setStatus(this.errorMessage, "error");
      return;
    }

    // Collect Turnstile token if present.
    const turnstileInput = this.form.querySelector(
      '[name="cf_turnstile_response"]',
    );
    const cfToken = turnstileInput ? turnstileInput.value : "";

    const nonceInput = this.form.querySelector('[name="nonce"]');
    const pageUrlInput = this.form.querySelector('[name="page_url"]');

    const payload = {
      nonce: nonceInput ? nonceInput.value : "",
      name: this.fields.name.value.trim(),
      email: this.fields.email.value.trim(),
      message: this.fields.message.value.trim(),
      recipient: this.recipient,
      subject_template: this.subjectTemplate,
      page_url: pageUrlInput ? pageUrlInput.value : "",
      cf_turnstile_response: cfToken,
    };

    this.setBusy(true);

    try {
      const res = await fetch(this.restUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin",
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data && data.success) {
        // Swap form for success message.
        this.replaceWithSuccess();
        return;
      }

      // Per-field errors from server.
      if (data && data.errors && typeof data.errors === "object") {
        Object.entries(data.errors).forEach(([k, v]) => this.setFieldError(k, v));
      }

      this.setStatus(
        (data && data.message) || this.errorMessage,
        "error",
      );

      // Reset Turnstile so the user can retry.
      if (window.turnstile && typeof window.turnstile.reset === "function") {
        window.turnstile.reset(this.form.querySelector(".cf-turnstile"));
      }
    } catch (err) {
      this.setStatus(this.errorMessage, "error");
    } finally {
      this.setBusy(false);
    }
  }

  replaceWithSuccess() {
    const wrap = document.createElement("div");
    wrap.className = "form-success";
    wrap.setAttribute("role", "status");
    wrap.textContent = this.successMessage;
    this.form.replaceWith(wrap);
  }
}

/**
 * Initialize all `[data-contact-form]` forms on the page.
 */
export function initContactForms() {
  const forms = document.querySelectorAll("[data-contact-form]");
  forms.forEach((form) => new ContactForm(form));
}
